import { useCallback, useRef, useState } from 'react';
import classNames from 'classnames/bind';

import { CoreIcon } from '@autopass-web/mochi-icons';

import checkedIcon from './check-box-checked.svg';
import uncheckedIcon from './check-box-unchecked.svg';
import styles from './mochi-checkbox.module.scss';

const cx = classNames.bind(styles);

export interface MochiCheckboxProps {
  /**
   * The value of the checkbox
   */
  value: string;
  /**
   * Whether the checkbox is checked
   */
  checked: boolean;
  /**
   * The checked state of the checkbox when it is first rendered. Use this when you do not need to control the state of the checkbox.
   */
  defaultChecked?: boolean;
  /**
   * Children of the checkbox
   */
  children?: React.ReactNode;
  /**
   * The function to call when the checkbox is toggled
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const MochiCheckbox = ({
  value,
  checked,
  defaultChecked,
  children,
  onChange,
}: MochiCheckboxProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [_checked, setChecked] = useState(defaultChecked ?? false);
  const isControlled = checked !== undefined;

  const isChecked = checked ?? _checked;

  const handleCheckedChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = e.target.checked;

      if (!isControlled) {
        setChecked(newChecked);
        return;
      }

      onChange?.(e);
    },
    [onChange, isControlled],
  );

  return (
    <label
      className={cx('mochi-checkbox')}
      id={`checkbox-${value}::label`}
      htmlFor={`checkbox-${value}::input`}
    >
      <input
        id={`checkbox-${value}::input`}
        ref={inputRef}
        type="checkbox"
        hidden
        value={value}
        checked={checked}
        defaultChecked={defaultChecked}
        onChange={handleCheckedChange}
      />
      <span>
        <CoreIcon
          src={isChecked ? checkedIcon : uncheckedIcon}
          width={24}
          height={24}
        />
      </span>
      <span>{children}</span>
    </label>
  );
};
